// Число стран в подборе — из справочника, а не цифрой в тексте.
//
// ЗАЧЕМ. «19 стран» было зашито в превью приглашения и в картинки старта.
// Страны включают по одной (EE, CN на очереди), и каждая включённая делала
// тексты неправдой, пока кто-то не вспомнит их поправить.
//
// ЧТО СЧИТАЕТСЯ. Ровно те страны, которые анкета показывает открытыми:
// countryChoices() без пометки «пока не открыта». Одно правило на оба места —
// число в превью не может разойтись со списком в анкете.
//
// В ТЕКСТАХ — {countries}: «вузы {countries}», «{countries} universitetləri»,
// «universities in {countries}». Число неизвестно (справочник не прочитался) —
// фраза без числа: «разных стран», «müxtəlif ölkələrin», «different countries».

import { COUNTRY_CLOSED_MARK, countryChoices, type CountryRefRow } from "@/edu/answer-values";

export type CountLang = "ru" | "az" | "en";

export const COUNTRIES_TOKEN = "{countries}";

/** Сколько стран анкета показывает открытыми. Ноль — модуль закрыт. */
export function openCountryCount(rows: readonly CountryRefRow[] | null | undefined): number {
  if (!rows?.some((r) => r.is_active)) return 0;
  return countryChoices(rows).filter((c) => c.countryCode !== null && !c.label.endsWith(COUNTRY_CLOSED_MARK)).length;
}

const RU = new Intl.PluralRules("ru");
const EN = new Intl.PluralRules("en");

/**
 * Число со словом в той форме, которой его зовут тексты:
 *   ru — родительный: «вузы 21 страны», «вузы 22 стран», «вузы 25 стран»;
 *   az — «21 ölkənin» (форма от числа не зависит);
 *   en — «1 country», «19 countries».
 */
export function countriesPhrase(lang: CountLang, n: number | null | undefined): string {
  const ok = typeof n === "number" && Number.isInteger(n) && n > 0;
  if (lang === "ru") return ok ? `${n} ${RU.select(n) === "one" ? "страны" : "стран"}` : "разных стран";
  if (lang === "az") return ok ? `${n} ölkənin` : "müxtəlif ölkələrin";
  return ok ? `${n} ${EN.select(n) === "one" ? "country" : "countries"}` : "different countries";
}

/** Подставить число стран вместо {countries}. */
export function withCountries(text: string, lang: CountLang, n: number | null | undefined): string {
  return text.split(COUNTRIES_TOKEN).join(countriesPhrase(lang, n));
}

/** Токен именительной формы: «в подборе {countriesNom}» → «в подборе 22 страны» (FAQ лендинга, 17.10.2026). */
export const COUNTRIES_NOM_TOKEN = "{countriesNom}";

/**
 * Число стран в именительном падеже: ru «21 страна / 22 страны / 25 стран»,
 * az «22 ölkə», en «1 country / 22 countries». Нет числа — без числа:
 * «разные страны», «müxtəlif ölkələr», «a range of countries».
 */
export function countriesNominative(lang: CountLang, n: number | null | undefined): string {
  const ok = typeof n === "number" && Number.isInteger(n) && n > 0;
  if (lang === "ru") {
    if (!ok) return "разные страны";
    const f = RU.select(n);
    return `${n} ${f === "one" ? "страна" : f === "few" ? "страны" : "стран"}`;
  }
  if (lang === "az") return ok ? `${n} ölkə` : "müxtəlif ölkələr";
  return ok ? `${n} ${EN.select(n) === "one" ? "country" : "countries"}` : "a range of countries";
}

/** Подставить именительную форму вместо {countriesNom}. */
export function withCountriesNom(text: string, lang: CountLang, n: number | null | undefined): string {
  return text.split(COUNTRIES_NOM_TOKEN).join(countriesNominative(lang, n));
}
